import { useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { normalizeMuscleGroups } from '@/features/workouts/api/muscleGroups';
import { MuscleGroupSelect } from '@/features/workouts/ui/MuscleGroupSelect';
import type { MovementPattern, ExerciseEquipment } from '@/types';

export interface ExerciseFormState {
  name: string;
  description: string;
  movementPattern?: MovementPattern;
  equipmentTags?: ExerciseEquipment[];
  targetMuscleGroups?: string[];
  howTo: string;
  defaultCues: string;
  defaultSets: number;
  defaultReps: number;
  defaultRestSec: number;
}

interface ExerciseFormProps {
  form: ExerciseFormState;
  onChange: (value: ExerciseFormState) => void;
  onSubmit: () => void;
  mode?: 'create' | 'edit';
  onCancelEdit?: () => void;
  hideActions?: boolean;
}

const MOVEMENT_PATTERN_LABELS: Record<MovementPattern, string> = {
  squat: 'Squat',
  hinge: 'Hinge',
  lunge: 'Lunge',
  push_horizontal: 'Push (Horizontal)',
  push_vertical: 'Push (Vertical)',
  pull_horizontal: 'Pull (Horizontal)',
  pull_vertical: 'Pull (Vertical)',
  core: 'Core',
  carry: 'Carry',
  conditioning: 'Conditioning',
  warmup: 'Warmup',
};

const EQUIPMENT_LABELS: Record<ExerciseEquipment, string> = {
  bodyweight: 'Bodyweight',
  dumbbell: 'Dumbbell',
  barbell: 'Barbell',
  kettlebell: 'Kettlebell',
  cable: 'Cable',
  machine: 'Machine',
  band: 'Resistance Band',
};

export function ExerciseForm({
  form,
  onChange,
  onSubmit,
  mode = 'create',
  onCancelEdit,
  hideActions = false,
}: ExerciseFormProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    setTouched(true);
    
    // Validate required fields
    if (!form.name?.trim()) {
      setValidationError('Exercise name is required');
      return;
    }
    
    setValidationError(null);
    onSubmit();
  };

  const nameIsInvalid = touched && !form.name?.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Validation Error Banner */}
      {validationError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 text-sm">
          {validationError}
        </div>
      )}

      {/* Exercise Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Exercise Name <span className="text-red-500">*</span>
        </label>
        <input
          data-testid="exercise-name-input"
          className={`form-field ${nameIsInvalid ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
          placeholder="e.g. Barbell Squat"
          value={form.name}
          onChange={(e) => {
            onChange({ ...form, name: e.target.value });
            if (validationError && e.target.value.trim()) {
              setValidationError(null);
            }
          }}
          onBlur={() => setTouched(true)}
          required
          aria-required="true"
          aria-invalid={nameIsInvalid}
        />
        {nameIsInvalid && (
          <p className="mt-1 text-xs text-red-500">Exercise name is required</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Description
        </label>
        <textarea
          data-testid="exercise-description-input"
          className="form-textarea max-w-none"
          placeholder="Brief description of the exercise"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          rows={3}
        />
      </div>

      {/* Movement Pattern */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Movement Pattern
        </label>
        <select
          data-testid="exercise-movement-pattern-select"
          className="form-field"
          value={form.movementPattern || ''}
          onChange={(e) =>
            onChange({
              ...form,
              movementPattern: e.target.value
                ? (e.target.value as MovementPattern)
                : undefined,
            })
          }
        >
          <option value="">— Select Pattern —</option>
          {(Object.keys(MOVEMENT_PATTERN_LABELS) as MovementPattern[]).map(
            (pattern) => (
              <option key={pattern} value={pattern}>
                {MOVEMENT_PATTERN_LABELS[pattern]}
              </option>
            )
          )}
        </select>
      </div>

      {/* Equipment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Equipment
        </label>
        <div className="space-y-2">
          {(Object.keys(EQUIPMENT_LABELS) as ExerciseEquipment[]).map(
            (equipment) => (
              <label
                key={equipment}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer"
              >
                <input
                  data-testid={`exercise-equipment-${equipment}`}
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-white/10 text-primary-500 focus:ring-primary-500/50"
                  checked={form.equipmentTags?.includes(equipment) || false}
                  onChange={(e) => {
                    const current = form.equipmentTags || [];
                    const updated = e.target.checked
                      ? [...current, equipment]
                      : current.filter((eq) => eq !== equipment);
                    onChange({ ...form, equipmentTags: updated });
                  }}
                />
                {EQUIPMENT_LABELS[equipment]}
              </label>
            )
          )}
        </div>
      </div>

      {/* Target Muscle Groups */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Target Muscle Groups
        </label>
        <MuscleGroupSelect
          value={normalizeMuscleGroups(form.targetMuscleGroups)}
          onChange={(targetMuscleGroups) => onChange({ ...form, targetMuscleGroups })}
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
          Used to find compatible swaps for clients during workouts.
        </p>
      </div>

      {/* How To */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          How To (Step-by-Step)
        </label>
        <textarea
          data-testid="exercise-how-to-input"
          className="form-textarea max-w-none"
          placeholder="1. Setup position&#10;2. Execute movement&#10;3. Return to start"
          value={form.howTo}
          onChange={(e) => onChange({ ...form, howTo: e.target.value })}
          rows={4}
        />
      </div>

      {/* Default Cues */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Default Cues
        </label>
        <textarea
          data-testid="exercise-default-cues-input"
          className="form-textarea max-w-none"
          placeholder="Coaching cues and form reminders"
          value={form.defaultCues}
          onChange={(e) => onChange({ ...form, defaultCues: e.target.value })}
          rows={3}
        />
      </div>

      {/* Default Parameters */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Default Parameters
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
              Sets
            </label>
            <input
              data-testid="exercise-default-sets-input"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field"
              placeholder="3"
              value={form.defaultSets}
              onChange={(e) =>
                onChange({ ...form, defaultSets: Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
              Reps
            </label>
            <input
              data-testid="exercise-default-reps-input"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field"
              placeholder="8"
              value={form.defaultReps}
              onChange={(e) =>
                onChange({ ...form, defaultReps: Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
              Rest (sec)
            </label>
            <input
              data-testid="exercise-default-rest-input"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field"
              placeholder="90"
              value={form.defaultRestSec}
              onChange={(e) =>
                onChange({ ...form, defaultRestSec: Number(e.target.value) })
              }
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      {!hideActions && (
        <div className="flex flex-col gap-2 pt-4 border-t border-gray-200 dark:border-white/10">
          <Button type="submit" className="w-full" data-testid="exercise-save-button">
            {mode === 'edit' ? 'Save Changes' : 'Save to Library'}
          </Button>
          {mode === 'edit' && onCancelEdit && (
            <Button type="button" variant="secondary" onClick={onCancelEdit} className="w-full">
              Cancel
            </Button>
          )}
        </div>
      )}
    </form>
  );
}
